import type { ParticlePreset, SubKind } from "daedalus-core/effects";
import { STORM_RAIN_SLANT } from "./wind-presets.ts";
import type { WindHeading } from "./wind.ts";

/**
 * The two rain presets of the game's effects library (ADR-144 decision A4, ADR-141): drops falling as streaks across a
 * screen-anchored area. One emitter holds the preset's steady count, so rain is drawn by many emitters side by side, one per
 * strip of the screen (`LIGHT_RAIN_STRIPS`, `HEAVY_RAIN_STRIPS`).
 */

const LIGHT_DROP: SubKind = {
  share: 1,
  life: [550, 700],
  size: [{ px: 1, weight: 1 }],
  speed: [380, 460],
  direction: 98,
  spread: 4,
  gravity: 0,
  streak: 5,
  sway: { amplitude: 0, hz: 0 },
  colours: [
    { at: 0, rgb: 0xdcebff },
    { at: 1, rgb: 0xa9c4ea },
  ],
  alpha: [
    { at: 0, value: 0.85 },
    { at: 1, value: 0.6 },
  ],
  blend: "normal",
  emissive: false,
};

const HEAVY_DROP: SubKind = {
  ...LIGHT_DROP,
  life: [450, 600],
  speed: [500, 600],
  direction: 104,
  spread: 6,
  streak: 8,
  colours: [
    { at: 0, rgb: 0xd0e2fb },
    { at: 1, rgb: 0x9bb8e4 },
  ],
  alpha: [
    { at: 0, value: 0.95 },
    { at: 1, value: 0.7 },
  ],
};

/**
 * The light rain preset.
 */
export const RAIN_PRESET: ParticlePreset = { id: "rain", name: "Rain", group: "weather", words: ["rain", "drops", "weather"], kind: "particles", rate: 40, burst: 0, kinds: [LIGHT_DROP] };

/**
 * The heavy rain preset a storm draws.
 */
export const RAIN_HEAVY_PRESET: ParticlePreset = { id: "rain-heavy", name: "Downpour", group: "weather", words: ["rain", "storm", "downpour", "weather"], kind: "particles", rate: 60, burst: 0, kinds: [HEAVY_DROP] };

/**
 * Which way a rain leans: straight down, or slanted with a storm's wind.
 */
export type RainSlant = "none" | WindHeading;

const STREAK_GROWTH = 1.25;
const STRAIGHT_DOWN = 90;

/**
 * A rain preset slanted with the wind: its drops fall `STORM_RAIN_SLANT` degrees off straight down toward the heading (a
 * wind blowing right leans the rain right) and its streaks are a quarter longer.
 *
 * @param preset A rain preset.
 * @param heading The wind's heading.
 * @returns The slanted preset, with the id `<id>@<heading>`.
 */
export function slantedRain(preset: ParticlePreset, heading: WindHeading): ParticlePreset {
  const direction = heading === "right" ? STRAIGHT_DOWN - STORM_RAIN_SLANT : STRAIGHT_DOWN + STORM_RAIN_SLANT;
  return { ...preset, id: rainPresetId(preset.id, heading), kinds: preset.kinds.map((kind) => ({ ...kind, direction, streak: Math.round((kind.streak ?? 0) * STREAK_GROWTH) })) };
}

/**
 * The id of a rain preset under a slant.
 *
 * @param id The preset's own id.
 * @param slant The slant.
 * @returns The id itself for straight rain, else `<id>@<heading>`.
 */
export function rainPresetId(id: string, slant: RainSlant): string {
  return slant === "none" ? id : `${id}@${slant}`;
}

/**
 * Every rain preset, by id: the two straight ones and each slanted both ways.
 */
export const RAIN_PRESETS: Readonly<Record<string, ParticlePreset>> = Object.freeze(
  Object.fromEntries([RAIN_PRESET, RAIN_HEAVY_PRESET].flatMap((preset) => [preset, slantedRain(preset, "left"), slantedRain(preset, "right")]).map((preset) => [preset.id, preset])),
);
